import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ClipboardX, BellOff, FileQuestion, Check, ArrowRight, CalendarClock } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo } from "@/components/Layout";
import { SiteProvider, useSite } from "@/lib/site";
import CallbackForm from "@/components/CallbackForm";
import ReserveDialog from "@/components/ReserveDialog";
import TransferDialog from "@/components/TransferDialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TextsProvider, E, EditBanner } from "@/lib/texts";

const leftLabel = ({ total, left }) => left === 0 ? "Offre fondateur complète"
  : left === total ? `${total} places disponibles pour l'offre fondateur`
  : `${left === 1 ? "Plus qu'une place" : `Plus que ${left} places`} sur ${total} pour l'offre fondateur`;

const scrollToOffer = () => document.getElementById("offre")?.scrollIntoView({ behavior: "smooth", block: "start" });

const PROBLEMS = [ClipboardX, BellOff, FileQuestion];
const SHOTS = ["/landing/tableau-de-bord.jpg", "/landing/fiche-sponsor.jpg", "/landing/bilan.jpg"];

const Section = ({ id, eyebrow, title, children, className = "" }) => (
  <section id={id} className={`max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-24 ${className}`}>
    {eyebrow && <p className="text-xs font-bold tracking-[0.14em] uppercase text-[#1E6B45]">{eyebrow}</p>}
    {title && <h2 className="text-[28px] sm:text-4xl font-bold tracking-tight mt-3 max-w-[720px] leading-[1.1]">{title}</h2>}
    {children}
  </section>
);

function TopBar() {
  return (
    <header className="bg-[#13201A] sticky top-0 z-40">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 h-16 flex items-center gap-4">
        <Link to="/" data-testid="landing-logo"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-6 ml-8 text-sm font-semibold text-white/75">
          <a href="#probleme" className="hover:text-white"><E k="nav.problem" /></a>
          <a href="#solution" className="hover:text-white"><E k="nav.solution" /></a>
          <a href="#offre" className="hover:text-white"><E k="nav.offer" /></a>
          <a href="#faq" className="hover:text-white"><E k="nav.faq" /></a>
        </nav>
        <Link to="/connexion" className="ml-auto text-sm font-semibold text-white/85 hover:text-white" data-testid="landing-nav-login-link"><E k="nav.login" /></Link>
        <button onClick={scrollToOffer} className="hidden sm:inline-flex btn btn-sm bg-[#3DDC84] text-[#13201A] hover:bg-[#5BE69A]" data-testid="landing-nav-cta"><E k="nav.cta" /></button>
      </div>
    </header>
  );
}

const Frame = ({ src, alt, testId }) => (
  <figure className="m-0">
    <div className="rounded-[14px] border border-[#E1E4DD] bg-white shadow-[0_18px_50px_-20px_rgba(19,32,26,0.35)] overflow-hidden">
      <div className="h-7 bg-[#EEF0EB] flex items-center gap-1.5 px-3">
        {[0, 1, 2].map((i) => <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#D5D9D1]" />)}
      </div>
      <img src={src} alt={alt} loading="lazy" className="w-full block" data-testid={testId} />
    </div>
    <figcaption className="text-xs text-[#5B665F] mt-2.5"><E k="shot.caption" /></figcaption>
  </figure>
);

function Hero({ seats }) {
  return (
    <section className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 pt-12 sm:pt-20 pb-10" data-testid="landing-hero">
      <div className="max-w-[820px] fade-up">
        <p className="inline-flex items-center gap-2 text-xs font-semibold bg-[#DFF3E6] text-[#145A36] rounded-full px-3 py-1"><E k="hero.badge" /></p>
        <h1 className="text-[40px] leading-[1.02] sm:text-6xl lg:text-7xl font-bold tracking-tight mt-5" data-testid="landing-title"><E k="hero.title" /></h1>
        <p className="text-base sm:text-lg text-[#5B665F] mt-6 max-w-[620px] leading-relaxed" data-testid="landing-subtitle"><E k="hero.subtitle" /></p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-8">
          <button onClick={scrollToOffer} className="btn-primary h-12 px-6 text-[15px] w-full sm:w-auto" data-testid="landing-hero-cta"><E k="hero.cta" /> <ArrowRight size={17} /></button>
          <Link to="/connexion" className="text-[15px] font-semibold link text-center" data-testid="landing-hero-login-link"><E k="hero.login" /></Link>
        </div>
        <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#8A4B00]" data-testid="landing-hero-seats">
          <span className="w-2 h-2 rounded-full bg-[#E08A1E] animate-pulse" />{leftLabel(seats)}
        </p>
      </div>
      <div className="mt-12 sm:mt-16 fade-up" style={{ animationDelay: "120ms" }}>
        <Frame src="/landing/tableau-de-bord.jpg" alt="Tableau de bord Partenia" testId="landing-hero-shot" />
      </div>
    </section>
  );
}

function Problem() {
  return (
    <Section id="probleme" eyebrow={<E k="problem.eyebrow" />} title={<E k="problem.title" />}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
        {PROBLEMS.map((Icon, i) => (
          <article key={i} className="card p-6" data-testid={`landing-problem-${i}`}>
            <span className="grid place-items-center w-10 h-10 rounded-[10px] bg-[#FDEBD3] text-[#8A4B00]"><Icon size={19} /></span>
            <h3 className="text-lg font-bold mt-5 leading-snug"><E k={`problem.${i + 1}.title`} /></h3>
            <p className="text-sm text-[#5B665F] mt-2 leading-relaxed"><E k={`problem.${i + 1}.text`} /></p>
          </article>
        ))}
      </div>
    </Section>
  );
}

function Solution() {
  return (
    <Section id="solution" eyebrow={<E k="solution.eyebrow" />} title={<E k="solution.title" />} className="border-t border-[#E1E4DD]">
      <div className="space-y-16 sm:space-y-24 mt-12">
        {SHOTS.map((src, i) => (
          <div key={src} className={`grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-6 lg:gap-14 items-center`} data-testid={`landing-solution-${i}`}>
            <div className={i % 2 ? "lg:order-2" : ""}>
              <p className="num text-sm font-bold text-[#1E6B45]">0{i + 1}</p>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2"><E k={`solution.${i + 1}.title`} /></h3>
              <p className="text-[15px] sm:text-base text-[#5B665F] mt-3 leading-relaxed max-w-[440px]"><E k={`solution.${i + 1}.text`} /></p>
            </div>
            <div className={i % 2 ? "lg:order-1" : ""}><Frame src={src} alt="Capture de Partenia" testId={`landing-shot-${i}`} /></div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Offer({ seats, onReserve, onTransfer }) {
  const L = useSite();
  const { total, taken, left } = seats;
  return (
    <section id="offre" className="bg-[#13201A] text-white scroll-mt-16" data-testid="landing-offer">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <p className="text-xs font-bold tracking-[0.14em] uppercase text-[#3DDC84]"><E k="offer.eyebrow" /></p>
          <h2 className="text-[28px] sm:text-4xl font-bold tracking-tight mt-3 leading-[1.1]"><E k="offer.title" /></h2>
          <p className="text-white/70 mt-4 leading-relaxed max-w-[460px]"><E k="offer.text" /></p>
        </div>
        <div className="rounded-[14px] bg-white text-[#13201A] p-6 sm:p-8">
          <div className="flex items-end gap-3 flex-wrap">
            <span className="num text-5xl sm:text-6xl font-bold tracking-tight" data-testid="landing-offer-price">{left > 0 ? L.founderPrice : L.regularPrice}</span>
            {left > 0 && <span className="text-[#5B665F] line-through mb-2" data-testid="landing-offer-regular-price">{L.regularPrice}</span>}
          </div>
          <p className="text-sm text-[#5B665F] mt-1"><E k="offer.period" /></p>
          <div className="mt-6" data-testid="landing-seat-counter">
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-semibold" data-testid="landing-seats-left">{left === 0 ? "Toutes les places sont prises" : `${left} place${left > 1 ? "s" : ""} restante${left > 1 ? "s" : ""}`}</span>
              <span className="text-[#5B665F]">{taken} / {total} réservées</span>
            </div>
            <div className="flex gap-1 mt-2">
              {Array.from({ length: total }, (_, i) => <span key={i} className={`h-2 flex-1 rounded-full ${i < taken ? "bg-[#1E6B45]" : "bg-[#E9ECE6]"}`} />)}
            </div>
          </div>
          <ul className="mt-6 space-y-3">
            {[1, 2, 3, 4].map((n, i) => (
              <li key={n} className="flex gap-3 text-[15px]" data-testid={`landing-offer-item-${i}`}>
                <span className="grid place-items-center w-5 h-5 rounded-full bg-[#DFF3E6] text-[#1E6B45] shrink-0 mt-0.5"><Check size={13} /></span><span><E k={`offer.item${n}`} /></span>
              </li>
            ))}
          </ul>
          {left > 0
            ? (L.founderPaymentUrl
              ? <a href={L.founderPaymentUrl} target="_blank" rel="noopener noreferrer" className="btn-primary w-full h-12 text-[15px] mt-8" data-testid="landing-offer-reserve-button"><E k="offer.reserve" /></a>
              : L.liveMode
                ? <a href="#rappel" className="btn-primary w-full h-12 text-[15px] mt-8" data-testid="landing-offer-reserve-button"><E k="offer.reserve" /></a>
                : <button onClick={onReserve} className="btn-primary w-full h-12 text-[15px] mt-8" data-testid="landing-offer-reserve-button"><E k="offer.reserve" /></button>)
            : L.memberPaymentUrl
              ? <a href={L.memberPaymentUrl} target="_blank" rel="noopener noreferrer" className="btn-primary w-full h-12 text-[15px] mt-8" data-testid="landing-offer-member-button"><E k="offer.member" /></a>
              : <a href="#rappel" className="btn-outline w-full h-12 text-[15px] mt-8" data-testid="landing-offer-full-button"><E k="offer.full" /></a>}
          {left > 0 && <button onClick={onTransfer} className="block mx-auto mt-3 text-sm link" data-testid="landing-offer-transfer-link"><E k="offer.transfer" /></button>}
          <p className="text-xs text-[#5B665F] text-center mt-3"><E k="offer.secure" /></p>
          <p className="text-sm text-center mt-4"><E k="offer.question" /> <a href="#rappel" className="link" data-testid="landing-offer-callback-link"><E k="offer.callback" /></a></p>
        </div>
      </div>
    </section>
  );
}

function About() {
  const L = useSite();
  const f = L.founder;
  return (
    <Section id="qui" eyebrow={<E k="about.eyebrow" />} className="scroll-mt-16">
      <div className="card p-6 sm:p-10 mt-6 flex flex-col sm:flex-row gap-6 sm:gap-10 items-start" data-testid="landing-about">
        {f.photo ? <img src={f.photo} alt={f.name} className="w-24 h-24 rounded-full object-cover shrink-0" />
          : <span className="grid place-items-center w-24 h-24 rounded-full bg-[#13201A] text-[#3DDC84] font-display font-bold text-3xl shrink-0" data-testid="landing-about-initials">{f.initials}</span>}
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight"><E k="about.name" /></h2>
          <p className="text-sm font-semibold text-[#1E6B45] mt-1"><E k="about.role" /></p>
          <p className="text-[15px] text-[#2B3830] mt-4 leading-relaxed max-w-[640px] whitespace-pre-line"><E k="about.bio" /></p>
          <a href={L.calendlyUrl} target="_blank" rel="noopener noreferrer" className="btn-outline mt-6" data-testid="landing-about-calendly-button"><CalendarClock size={16} /> <E k="about.cta" /></a>
        </div>
      </div>
    </Section>
  );
}

function Callback() {
  return (
    <Section id="rappel" eyebrow={<E k="callback.eyebrow" />} title={<E k="callback.title" />} className="scroll-mt-16 pt-4 sm:pt-8">
      <p className="text-[15px] sm:text-base text-[#5B665F] mt-3 max-w-[560px] leading-relaxed"><E k="callback.text" /></p>
      <div className="mt-8 max-w-[860px]"><CallbackForm /></div>
    </Section>
  );
}

function Faq() {
  return (
    <Section id="faq" eyebrow={<E k="faq.eyebrow" />} title={<E k="faq.title" />} className="scroll-mt-16 pt-4 sm:pt-8">
      <Accordion type="single" collapsible className="card mt-8 px-5 sm:px-7" data-testid="landing-faq">
        {[1, 2, 3].map((n, i) => (
          <AccordionItem key={n} value={`q${i}`} className="border-[#E1E4DD] last:border-b-0">
            <AccordionTrigger className="text-left text-[15px] sm:text-base font-semibold hover:no-underline py-5" data-testid={`landing-faq-question-${i}`}><span><E k={`faq.${n}.q`} /></span></AccordionTrigger>
            <AccordionContent className="text-[15px] text-[#5B665F] leading-relaxed" data-testid={`landing-faq-answer-${i}`}><E k={`faq.${n}.a`} /></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
}

const LandingFooter = () => {
  const L = useSite();
  return (
  <footer className="border-t border-[#E1E4DD]" data-testid="landing-footer">
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-8 flex flex-col sm:flex-row sm:items-center gap-4 text-[13px] text-[#5B665F]">
      <Logo light={false} />
      <div className="flex flex-wrap gap-x-6 gap-y-2 sm:ml-auto">
        <Link to="/mentions-legales" className="hover:text-[#13201A]" data-testid="landing-footer-legal"><E k="footer.legal" /></Link>
        <Link to="/confidentialite" className="hover:text-[#13201A]" data-testid="landing-footer-privacy"><E k="footer.privacy" /></Link>
        <a href={`mailto:${encodeURIComponent(L.contactEmail)}`} className="hover:text-[#13201A]" data-testid="landing-footer-contact"><E k="footer.contact" /></a>
      </div>
    </div>
  </footer>
  );
};

export default function Landing() {
  const { session } = useAuth();
  const edit = session?.user?.platform_role === "admin";
  return <SiteProvider><TextsProvider edit={edit}>{edit && <EditBanner />}<LandingContent /></TextsProvider></SiteProvider>;
}

function LandingContent() {
  const [seats, setSeats] = useState({ total: 10, taken: 0, left: 10 });
  const [reserveOpen, setReserveOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const { hash } = useLocation();
  useEffect(() => { api.get("/founder/seats").then((r) => setSeats(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" }), 300); }, [hash]);
  return (
    <div className="min-h-screen scroll-smooth" data-testid="landing-page">
      <TopBar />
      <Hero seats={seats} />
      <Problem />
      <Solution />
      <Offer seats={seats} onReserve={() => setReserveOpen(true)} onTransfer={() => setTransferOpen(true)} />
      <About />
      <Callback />
      <Faq />
      <LandingFooter />
      <ReserveDialog open={reserveOpen} onOpenChange={setReserveOpen} />
      <TransferDialog open={transferOpen} onOpenChange={setTransferOpen} />
    </div>
  );
}
